import { useEffect, type FunctionComponent, type ReactElement, useMemo } from 'react'
import { type ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import {
  extractUserContextFromJWT,
  logAuthenticationError,
  decodeJWTPayload,
  ERROR_TYPES,
  AUTH_STAGES,
  FAILURE_REASONS,
} from '../../utils/auth'
import { errors } from '../../utils/error-handling/errors'
import { type FetchCurrentEnterpriseAccountQuery } from '../../__codegen__/api'
import {
  isNotAuthorizedError,
  isUndeterminedAccountError,
  isTokenExpiredError,
} from '../utils/apollo/errors/isError.hooks'
import { useSessionData } from '../utils/apollo/session/useSessionData.hooks'
import { type PartialCurrentEnterpriseAccount } from './currentAccount.types'
import {
  useCurrentAccountSplitLazyQuery,
  type CurrentAccountSplitLazyQueryOptions,
} from './currentAccountSplitQueries'

export type CurrentAccountDetailsProps = CurrentAccountSplitLazyQueryOptions

interface CurrentAccountDetailsValue {
  authorized: boolean
  actionRequired: boolean
  account: FetchCurrentEnterpriseAccountQuery['currentEnterpriseAccount']
  loading: boolean
  error?: ApolloError
  refetch: () => void
}

type TokenStatus = 'no-token' | 'valid' | 'expired' | 'corrupted'

const notAuthorizedAccountDetails: Omit<CurrentAccountDetailsValue, 'loading'> = {
  authorized: false,
  actionRequired: false,
  account: undefined,
  error: undefined,
  refetch: () => {},
}

/** Redirect to login page with an auth error reason, deferred to avoid redirecting mid-render. */
function redirectToLogin(reason: string) {
  setTimeout(() => {
    window.location.href = `/public/log-in?authError=${reason}`
  }, 0)
}

export const useCurrentAccountDetails = (
  options: CurrentAccountDetailsProps = {}
): CurrentAccountDetailsValue => {
  const { data: sessionData, setData: storeSessionData } = useSessionData()

  // Pure computation — no side effects. Determines the state of the JWT token.
  const tokenStatus: TokenStatus = useMemo(() => {
    if (!sessionData?.token) return 'no-token'
    const payload = decodeJWTPayload(sessionData.token)
    if (!payload || typeof payload.exp !== 'number') return 'corrupted'
    if (payload.exp * 1000 < Date.now()) return 'expired'
    return 'valid'
  }, [sessionData?.token])

  // Handle corrupted tokens: log to DataDog, clear session, and redirect to login.
  useEffect(() => {
    if (tokenStatus !== 'corrupted' || !sessionData?.token) return

    const userContext = extractUserContextFromJWT(sessionData.token)

    logAuthenticationError(
      new Error('Session cleared due to corrupted JWT'),
      ERROR_TYPES.JWT_PARSING_FAILURE,
      AUTH_STAGES.JWT_VALIDATION,
      FAILURE_REASONS.JWT_PARSE_ERROR,
      userContext,
      {
        tokenPreview: sessionData.token.substring(0, 20) + '...',
        jwtPartsCount: sessionData.token.split('.').length,
      }
    )

    storeSessionData(undefined)
    redirectToLogin('sessionCorrupted')
  }, [tokenStatus, sessionData?.token, storeSessionData])

  const hasValidSession = Boolean(sessionData && tokenStatus === 'valid')
  const hasSession = Boolean(sessionData)

  const [
    fetchAccount,
    { data: apiData, loading: apiLoading, error: apiError, called: apiCalled, refetch },
  ] = useCurrentAccountSplitLazyQuery(options)

  const notAuthorizedError = isNotAuthorizedError(apiError)
  const isSessionError =
    notAuthorizedError || isUndeterminedAccountError(apiError) || isTokenExpiredError(apiError)

  // If we have a session token (even if expired), make the API call.
  // The errorLink in clientUtilities.ts will handle token refresh if needed.
  useEffect(() => {
    if (hasSession) {
      fetchAccount()
    }
  }, [hasSession, fetchAccount])

  // Clear session on authorization errors (invalid token, disabled account, etc.).
  // We don't clear for expired tokens — the errorLink will attempt a refresh automatically.
  useEffect(() => {
    if (!notAuthorizedError) return

    const errorCode = apiError?.graphQLErrors?.[0]?.extensions?.code

    const userContext = sessionData?.token
      ? extractUserContextFromJWT(sessionData.token)
      : { email: null, emailError: 'no_session', userId: 'unknown' }

    logAuthenticationError(
      new Error(`Session cleared due to authorization error: ${errorCode}`),
      ERROR_TYPES.SESSION_CLEAR_EVENT,
      AUTH_STAGES.SESSION_MANAGEMENT,
      FAILURE_REASONS.AUTHORIZATION_ERROR,
      userContext,
      {
        errorCode,
        reason: FAILURE_REASONS.AUTHORIZATION_ERROR,
        hasApiError: !!apiError,
        isTokenExpired: tokenStatus === 'expired',
      }
    )

    storeSessionData(undefined)
  }, [notAuthorizedError, tokenStatus, apiError, sessionData, storeSessionData])

  const account = apiData?.currentEnterpriseAccount

  // Set DataDog RUM user identity when account data is available.
  useEffect(() => {
    if (!account) return

    try {
      datadogRum.setUser({
        id: account.id,
        canonicalId: account.canonicalId,
        isInternal: account.isInternal,
        isImpersonating: account.isImpersonating,
      })
    } catch (error) {
      errors.captureException(error as Error, 'Error setting Datadog RUM user details', {
        logToConsole: true,
      })
    }
  }, [account])

  // No valid session — we're done loading.
  if (!hasValidSession) {
    return { ...notAuthorizedAccountDetails, loading: false }
  }

  const loading = !apiCalled || apiLoading

  // Suppress session-related errors so the app stays in a "not logged in" state,
  // allowing NotLoggedInRedirect to handle the redirect to login.
  if (isSessionError) {
    return { ...notAuthorizedAccountDetails, loading }
  }

  const actionRequired = account != null && !account.accountStateValid

  return {
    authorized: Boolean(apiData),
    account,
    loading,
    error: apiError,
    actionRequired,
    refetch: refetch ?? (() => {}),
  }
}

type CurrentAccountWithChildren = (account: PartialCurrentEnterpriseAccount) => ReactElement
interface ICurrentAccountProps {
  children: CurrentAccountWithChildren
}

/**
 * Render-prop wrapper that passes the current account to its children.
 *
 * @deprecated Use the `useCurrentAccountDetails` hook directly instead.
 * Only kept for legacy class components (e.g. StripeAssociations.tsx).
 */
export const CurrentAccountDetails: FunctionComponent<
  React.PropsWithChildren<ICurrentAccountProps>
> = ({ children }) => {
  const { account } = useCurrentAccountDetails()

  return children(account)
}
